import React from 'react';
import { createPortal } from 'react-dom';
import { Modal } from '../../Common/Modal';

/**
 * Visit Report — Reviewer's Comment (DCF) modal (ported from CRFForm).
 * All behavior stays parent-driven via callbacks / flags.
 */
export default function DcfVisitReportModal({
  dcfModalOpen,
  onCloseModal,
  dcfInfo,
  loadingHistory,
  dcfHistory,
  dcfRemark,
  setDcfRemark,
  dcfRemarkError,
  setDcfRemarkError,
  dcfResponse,
  setDcfResponse,
  dcfSubmitting,
  submitAllDcfSubmitting,
  crfVisitReportNonTransactional,
  dcfStatusLower,
  showReleaseDcfsToCraInModal,
  showReviewerResolveOpenDcf,
  dcfMessages,
  dcfAlreadyRaisedOnField,
  canRaiseDCFFromServer,
  canRaiseNewDcf,
  canCloseDCF,
  dcfCloseBlockMessage,
  isResolved,
  isCraUser,
  craDcfModalShowResponseUi,
  craDcfModalCraResponseBlocked,
  craDcfModalCraResponseBlockedDetail,
  showReviewerDcfView,
  dcfBatchTierForReviewGate,
  reviewerAtDcfBatchTier,
  handleCRFDCFSubmit,
  handleFinalCloseDCF,
  handleSubmitAllDcfsToCra,
  handleResolveDCF,
  handleSubmitDCFResponse,
  formatPerformedOnUtcToLocal,
  formatOffsetForDisplay,
}) {
  if (!dcfModalOpen) return null;

  return createPortal(
    <div className="crf-nested-modal-wrapper" style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Modal
        isOpen
        onClose={onCloseModal}
        title="Reviewer's Comment Detail"
        showCloseButton
        className="crf-dcf-modal"
        overlayClassName="crf-nested-modal-overlay"
      >
        {dcfInfo.sentToReviewStage ? (
          <p className="text-muted small mb-2 px-1">
            Sent to Stage: <strong>{dcfInfo.sentToReviewStage}</strong>
          </p>
        ) : null}
        <div className="table-responsive crf-dcf-modal-table-wrap">
          <table className="table crf-dcf-modal-table">
            <thead>
              <tr>
                <th>Action</th>
                <th>Remarks</th>
                <th>Performed By</th>
                <th>Performed On UTC</th>
                <th>Performed At Offset</th>
              </tr>
            </thead>
            <tbody>
              {loadingHistory ? (
                <tr><td colSpan="5" className="text-center py-3">Loading history...</td></tr>
              ) : dcfHistory.length > 0 ? (
                dcfHistory.map((h, i) => {
                  const remarksCell = (h.remarks || h.Remarks) || '';
                  return (
                    <tr key={i}>
                      <td>{h.action || h.Action}</td>
                      <td>{remarksCell}</td>
                      <td>{h.performedBy || h.PerformedBy}</td>
                      <td>{h.performedOnUTC ? formatPerformedOnUtcToLocal(h.performedOnUTC || h.PerformedOnUTC, h.performedAtOffset || h.PerformedAtOffset) : ''}</td>
                      <td>{formatOffsetForDisplay(h.performedAtOffset || h.PerformedAtOffset)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="text-muted text-center py-3">
                    No Reviewer's Comment yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {showReviewerDcfView && !isCraUser ? (
          <form onSubmit={handleCRFDCFSubmit}>
            {!crfVisitReportNonTransactional && dcfStatusLower === 'open' && (
              <div className="alert alert-info mt-3" role="status">
                {showReleaseDcfsToCraInModal
                  ? dcfMessages.reviewerOpenReleaseBeforeCraResponse
                  : showReviewerResolveOpenDcf
                    ? dcfMessages.reviewerOpenResolveAtTierHint
                    : !dcfBatchTierForReviewGate
                      ? dcfMessages.reviewerOpenWaitingCra
                      : reviewerAtDcfBatchTier
                        ? dcfMessages.reviewerOpenRoutedReviewTierReviewerRemit
                        : dcfMessages.reviewerOpenWrongTierForUser.replace('{dcfStage}', dcfBatchTierForReviewGate)}
              </div>
            )}
            {!crfVisitReportNonTransactional && showReviewerResolveOpenDcf && (
              <div className="mt-4 crf-dcf-comment-box-wrap">
                <label className="form-label" htmlFor="crf-dcf-reviewer-resolve-remarks">
                  Remarks
                </label>
                <textarea
                  id="crf-dcf-reviewer-resolve-remarks"
                  className="form-control crf-dcf-comment-box"
                  rows={4}
                  value={dcfResponse}
                  onChange={(e) => setDcfResponse(e.target.value)}
                  aria-label="Remarks for Reviewer's Comment at this review tier"
                />
              </div>
            )}
            {!crfVisitReportNonTransactional &&
              dcfAlreadyRaisedOnField &&
              dcfStatusLower !== 'open' &&
              !(isResolved && canCloseDCF) && (
              <div className="alert alert-warning mt-3">
                {dcfCloseBlockMessage || dcfMessages.alreadyRaised}
              </div>
            )}
            {!crfVisitReportNonTransactional && canRaiseDCFFromServer && canRaiseNewDcf && (
              <div className="mt-4 crf-dcf-comment-box-wrap">
                <label className="form-label">
                  Remarks <span className="required-asterisk">*</span>
                </label>
                <textarea
                  className="form-control crf-dcf-comment-box"
                  rows={4}
                  value={dcfRemark}
                  onChange={(e) => { setDcfRemark(e.target.value); setDcfRemarkError(''); }}
                  aria-label="Remarks for raising Reviewer's Comment (Reviewer only)"
                />
                {dcfRemarkError && (
                  <div className="field-error text-danger mt-1" style={{ fontSize: '12px' }}>
                    {dcfRemarkError}
                  </div>
                )}
              </div>
            )}
            {!crfVisitReportNonTransactional && canCloseDCF && (
              <div className="alert alert-info mt-3">
                This Reviewer's Comment has been resolved. You can now Close it.
              </div>
            )}
            <div className="modal-footer crf-dcf-modal-footer">
              {!crfVisitReportNonTransactional && canRaiseDCFFromServer && canRaiseNewDcf && (
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={dcfSubmitting}
                >
                  {dcfSubmitting ? 'Creating...' : "Raise Reviewer's Comment"}
                </button>
              )}
              {!crfVisitReportNonTransactional && canCloseDCF && (
                <button type="button" className="btn btn-success" onClick={handleFinalCloseDCF} disabled={dcfSubmitting}>{dcfSubmitting ? 'Closing...' : "Close Reviewer's Comment"}</button>
              )}
              {!crfVisitReportNonTransactional && showReleaseDcfsToCraInModal && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => { void handleSubmitAllDcfsToCra(); }}
                  disabled={submitAllDcfSubmitting || dcfSubmitting}
                >
                  {submitAllDcfSubmitting ? 'Submitting...' : "Submit All Reviewer's Comments"}
                </button>
              )}
              {!crfVisitReportNonTransactional && showReviewerResolveOpenDcf && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => { handleResolveDCF(); }}
                  disabled={dcfSubmitting}
                >
                  {dcfSubmitting ? 'Saving...' : 'Submit'}
                </button>
              )}

              <button type="button" className="btn btn-secondary" onClick={onCloseModal}>{crfVisitReportNonTransactional ? 'Close' : 'Cancel'}</button>
            </div>
          </form>
        ) : (
          <>
            {craDcfModalShowResponseUi ? (
              <>
                {isCraUser && craDcfModalCraResponseBlocked && !crfVisitReportNonTransactional ? (
                  <div className="mt-4 crf-dcf-comment-box-wrap" aria-live="polite">
                    <div
                      className="alert alert-warning mb-0 crf-dcf-comment-box d-flex flex-column justify-content-center"
                      style={{ minHeight: '120px' }}
                      role="alert"
                    >
                      <strong className="d-block mb-2">Response not available</strong>
                      <p className="mb-0 small" style={{ whiteSpace: 'pre-wrap' }}>{craDcfModalCraResponseBlockedDetail}</p>
                    </div>
                  </div>
                ) : null}
                {!(isCraUser && isResolved) && !crfVisitReportNonTransactional && !craDcfModalCraResponseBlocked ? (
                  <div className="mt-4 crf-dcf-comment-box-wrap">
                    <label className="form-label">
                      Remarks <span className="required-asterisk">*</span>
                    </label>
                    <textarea
                      className="form-control crf-dcf-comment-box"
                      rows={4}
                      value={dcfResponse}
                      onChange={(e) => setDcfResponse(e.target.value)}
                      style={{ resize: 'vertical', minHeight: '100px' }}
                      readOnly={crfVisitReportNonTransactional}
                      aria-label="Reviewer's Comment response comment (CRA)"
                    />
                  </div>
                ) : null}
                <div className="modal-footer crf-dcf-modal-footer">
                  {!(isCraUser && isResolved) && !crfVisitReportNonTransactional && !craDcfModalCraResponseBlocked ? (
                    <button type="button" className="btn btn-primary" onClick={handleSubmitDCFResponse} disabled={dcfSubmitting}>{dcfSubmitting ? 'Saving...' : 'Submit'}</button>
                  ) : null}
                  <button type="button" className="btn btn-secondary" onClick={onCloseModal}>Close</button>
                </div>
              </>
            ) : (
              <div className="modal-footer crf-dcf-modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onCloseModal}>Close</button>
              </div>
            )}
          </>
        )}
      </Modal>
    </div>,
    document.body
  );
}
